import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
  Box,
  Typography,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Link as MuiLink,
  IconButton,
  Divider,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import {
  Player,
  ExistingPlayerRegistrationRequest,
  GearTshirt,
  GearShorts,
  GearSocks,
  GearDesign,
  PlayerStats,
  Gender,
} from "../types";
import { apiRequest } from "../config/api";
import GearDesignPicker from "./GearDesignPicker";
import {
  getPromotionSpots,
  getRelegationSpots,
  getPromotionTarget,
  getRelegationTarget,
  getEffectivePromotionSpots,
} from "../utils/seasonTransition";
import { useSeason } from "../contexts/SeasonContext";

interface PlayerSeasonRegistrationDialogProps {
  open: boolean;
  onClose: () => void;
  player: Player;
  playerToken: string;
  onSuccess?: () => void;
}

export default function PlayerSeasonRegistrationDialog({
  open,
  onClose,
  player,
  playerToken,
  onSuccess,
}: PlayerSeasonRegistrationDialogProps) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [nationality, setNationality] = useState("");
  const [gender, setGender] = useState<Gender | "">("");
  const [isDPVRegistered, setIsDPVRegistered] = useState(false);
  const [wantsGear, setWantsGear] = useState(false);
  const [wantsTrialDay, setWantsTrialDay] = useState(false);
  const [preferredDivision, setPreferredDivision] = useState("");
  const [preferredDivisionReason, setPreferredDivisionReason] = useState("");

  // Level-assessment questions — asked instead of the division questions above
  // when the player has no division history yet (tournament-only account).
  const [preferredDivisionLevel, setPreferredDivisionLevel] = useState<
    "beginner" | "beginner+" | "intermediate" | "advanced" | "pro"
  >("beginner");
  const [levelReason, setLevelReason] = useState("");
  const [playtomicLevel, setPlaytomicLevel] = useState("");
  const [hasTournamentExperience, setHasTournamentExperience] =
    useState(false);
  const [hearAboutUsSource, setHearAboutUsSource] = useState("");
  const [hasReferencePlayer, setHasReferencePlayer] = useState(false);
  const [referencePlayerName, setReferencePlayerName] = useState("");
  const [gearTshirts, setGearTshirts] = useState<GearTshirt[]>([]);
  const [gearShorts, setGearShorts] = useState<GearShorts[]>([]);
  const [gearSocks, setGearSocks] = useState<GearSocks[]>([]);
  const [gearDesigns, setGearDesigns] = useState<GearDesign[]>([]);
  const tshirtDesigns = gearDesigns.filter(
    (d) => (d.category || "tshirt") === "tshirt",
  );
  const poloDesigns = gearDesigns.filter((d) => d.category === "polo");
  const shortsDesigns = gearDesigns.filter((d) => d.category === "shorts");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [rankings, setRankings] = useState<PlayerStats[]>([]);
  const [aboveDivisionRankings, setAboveDivisionRankings] = useState<
    PlayerStats[]
  >([]);
  const [loadingRankings, setLoadingRankings] = useState(true);
  const { activeSeason } = useSeason();

  const [tshirtPrice, setTshirtPrice] = useState<number | null>(null);
  const [shortsPrice, setShortsPrice] = useState<number | null>(null);
  const [kitPrice, setKitPrice] = useState<number | null>(null);
  const [tshirtSizeChartUrl, setTshirtSizeChartUrl] = useState("");
  const [poloSizeChartUrl, setPoloSizeChartUrl] = useState("");
  const [shortsSizeChartUrl, setShortsSizeChartUrl] = useState("");
  const [estimatedPrice, setEstimatedPrice] = useState<number | null>(null);
  const [seasonName, setSeasonName] = useState<string>("");
  const [midSeasonIsOpen, setMidSeasonIsOpen] = useState(false);
  const [midSeasonPrice, setMidSeasonPrice] = useState<number | null>(null);
  const [midSeasonGearEnabled, setMidSeasonGearEnabled] = useState(false);

  // Mid-season takes precedence if both windows happen to be open
  const activePhase: "initial" | "mid_season" = midSeasonIsOpen
    ? "mid_season"
    : "initial";
  const gearAllowed = activePhase !== "mid_season" || midSeasonGearEnabled;

  const [voucherCode, setVoucherCode] = useState("");
  const [voucherStatus, setVoucherStatus] = useState<
    "idle" | "checking" | "valid" | "invalid"
  >("idle");
  const [voucherMessage, setVoucherMessage] = useState("");

  // Tournament-only accounts have no division history, so they answer the
  // same level-assessment questions as brand-new players instead of the
  // division-projection questions below.
  const isTournamentOnly = !!player?.isTournamentOnly;

  // Pre-fill email, phone, and nationality from player profile
  useEffect(() => {
    if (player?.email) {
      setEmail(player.email);
    }
    if (player?.phone) {
      setPhone(player.phone);
    }
    if (player?.nationality) {
      setNationality(player.nationality);
    }
    if (player?.gender) {
      setGender(player.gender);
    }
  }, [player]);

  // Fetch rankings for player's division to calculate projected division
  useEffect(() => {
    const fetchRankings = async () => {
      if (!player || !open || !activeSeason?.id || isTournamentOnly) return;

      try {
        setLoadingRankings(true);
        const params = new URLSearchParams({
          division: player.division,
          seasonId: activeSeason.id,
        });
        // Use the public rankings endpoint — it's the same data source (and
        // penalty/bonus adjustments) that drives the public standings page,
        // so the projection here matches what players see there.
        const response = await apiRequest(
          `/api/public/rankings?${params.toString()}`,
        );
        if (response.ok) {
          const data = await response.json();
          setRankings(data);
        }

        // Also fetch rankings for the division above, so we can tell if it
        // has extra low-win-rate relegations that should open up extra
        // promotion spots here (e.g. 3 relegated from Master instead of the
        // usual 2 means 3 should be promoted from Division 1, not 2).
        const aboveDivision = getPromotionTarget(player.division);
        if (aboveDivision) {
          const aboveParams = new URLSearchParams({
            division: aboveDivision,
            seasonId: activeSeason.id,
          });
          const aboveResponse = await apiRequest(
            `/api/public/rankings?${aboveParams.toString()}`,
          );
          if (aboveResponse.ok) {
            const aboveData = await aboveResponse.json();
            setAboveDivisionRankings(aboveData);
          }
        } else {
          setAboveDivisionRankings([]);
        }
      } catch (error) {
        console.error("Error fetching rankings:", error);
      } finally {
        setLoadingRankings(false);
      }
    };

    fetchRankings();
  }, [player, open, activeSeason?.id, isTournamentOnly]);

  // Fetch estimated price, gear price, and the current gear design catalog
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const [response, designsRes] = await Promise.all([
          apiRequest("/api/season-registration/status"),
          apiRequest("/api/season-registration/gear-designs"),
        ]);
        if (response.ok) {
          const data = await response.json();
          if (data.estimatedPrice) {
            setEstimatedPrice(data.estimatedPrice);
          }
          if (data.tshirtPrice) {
            setTshirtPrice(data.tshirtPrice);
          }
          if (data.shortsPrice) {
            setShortsPrice(data.shortsPrice);
          }
          if (data.kitPrice) {
            setKitPrice(data.kitPrice);
          }
          setTshirtSizeChartUrl(data.tshirtSizeChartUrl || "");
          setPoloSizeChartUrl(data.poloSizeChartUrl || "");
          setShortsSizeChartUrl(data.shortsSizeChartUrl || "");
          if (data.seasonName) {
            setSeasonName(data.seasonName);
          }
          setMidSeasonIsOpen(data.midSeasonIsOpen || false);
          setMidSeasonPrice(data.midSeasonPrice ?? null);
          setMidSeasonGearEnabled(data.midSeasonGearEnabled || false);
        }
        if (designsRes.ok) {
          setGearDesigns(await designsRes.json());
        }
      } catch (error) {
        console.error("Error fetching prices:", error);
      }
    };

    if (open) {
      fetchPrices();
    }
  }, [open]);

  // Same low-win-rate relegation rule shown on the public standings page:
  // a player under 40% win rate after 2+ league days is relegated regardless
  // of table position.
  const WIN_RATE_RELEGATION_THRESHOLD = 40;
  const MIN_LEAGUE_DAYS_FOR_WIN_RATE_RELEGATION = 2;

  // Calculate projected division based on current rankings — mirrors the
  // eligibility/zone logic on the public standings page so this matches what
  // players see there.
  const getProjectedDivision = () => {
    if (!player || rankings.length === 0 || loadingRankings) {
      return null;
    }

    // Use the player's registered division
    const playerDivision = player.division;

    // Only players registered in this division, excluding both guests and
    // players who haven't played a game yet, count toward positioning.
    const eligiblePlayers = rankings.filter(
      (p) =>
        p.division === playerDivision &&
        !p.isGuest &&
        !p.isGuestPlayer &&
        p.gamesPlayed > 0,
    );

    // Find player's position among eligible players in their registered division
    const playerIndex = eligiblePlayers.findIndex(
      (p) => p.playerId === player.id,
    );
    if (playerIndex === -1) {
      return null;
    }

    const position = playerIndex + 1;
    const totalEligiblePlayers = eligiblePlayers.length;
    // Bumped up if the division above has extra low-win-rate relegations
    // beyond its usual "bottom N", so extra promotion spots open up here.
    const promotionSpots = getEffectivePromotionSpots(
      playerDivision,
      aboveDivisionRankings,
    );
    const relegationSpots = getRelegationSpots(playerDivision);
    const promotionTarget = getPromotionTarget(playerDivision);
    const relegationTarget = getRelegationTarget(playerDivision);

    const thisPlayer = eligiblePlayers[playerIndex];
    const isLowWinRateRelegation =
      thisPlayer.leagueDaysPlayed >= MIN_LEAGUE_DAYS_FOR_WIN_RATE_RELEGATION &&
      (thisPlayer.winPercentage ?? 100) < WIN_RATE_RELEGATION_THRESHOLD;

    // Check for promotion (among eligible players only)
    if (position <= promotionSpots && promotionTarget) {
      return {
        type: "promoted" as const,
        currentDivision: playerDivision,
        newDivision: promotionTarget,
        position,
        totalPlayers: totalEligiblePlayers,
      };
    }

    // Check for relegation: bottom spots by position, or low win rate regardless of position
    const isPositionRelegation =
      relegationSpots > 0 && position > totalEligiblePlayers - relegationSpots;
    if (relegationTarget && (isPositionRelegation || isLowWinRateRelegation)) {
      return {
        type: "relegated" as const,
        currentDivision: playerDivision,
        newDivision: relegationTarget,
        position,
        totalPlayers: totalEligiblePlayers,
        // Position-based relegation is a fixed "Bottom N" cutoff; low-win-rate
        // relegation can pull in players above that cutoff too, so the two
        // need different explanations in the UI.
        relegationReason: isPositionRelegation
          ? ("position" as const)
          : ("lowWinRate" as const),
      };
    }

    // Player stays
    return {
      type: "stays" as const,
      currentDivision: playerDivision,
      newDivision: playerDivision,
      position,
      totalPlayers: totalEligiblePlayers,
    };
  };

  const projectedDivision = getProjectedDivision();

  const addTshirt = () => {
    setGearTshirts([
      ...gearTshirts,
      { size: "M", shirtType: "tshirt", nameOnShirt: "" },
    ]);
  };

  const removeTshirt = (index: number) => {
    setGearTshirts(gearTshirts.filter((_, i) => i !== index));
  };

  const updateTshirt = (
    index: number,
    field: keyof GearTshirt,
    value: string,
  ) => {
    const updated = [...gearTshirts];
    updated[index] = { ...updated[index], [field]: value };
    // The design catalog is split by shirt type, so a design picked under one
    // type never carries over as a valid designId under the other.
    if (field === "shirtType") {
      delete updated[index].designId;
      delete updated[index].designName;
    }
    setGearTshirts(updated);
  };

  const updateTshirtDesign = (index: number, designId: string) => {
    const design = (
      gearTshirts[index]?.shirtType === "polo" ? poloDesigns : tshirtDesigns
    ).find((d) => d.id === designId);
    const updated = [...gearTshirts];
    updated[index] = {
      ...updated[index],
      designId: design?.id,
      designName: design?.name,
    };
    setGearTshirts(updated);
  };

  const updateShortsDesign = (index: number, designId: string) => {
    const design = shortsDesigns.find((d) => d.id === designId);
    const updated = [...gearShorts];
    updated[index] = {
      ...updated[index],
      designId: design?.id,
      designName: design?.name,
    };
    setGearShorts(updated);
  };

  const addShorts = () => {
    setGearShorts([...gearShorts, { size: "M" }]);
    setGearSocks([...gearSocks, { size: "" }]);
  };

  const removeShorts = (index: number) => {
    setGearShorts(gearShorts.filter((_, i) => i !== index));
    setGearSocks(gearSocks.filter((_, i) => i !== index));
  };

  const updateShorts = (
    index: number,
    size: "S" | "M" | "L" | "XL" | "XXL",
  ) => {
    const updated = [...gearShorts];
    updated[index] = { size };
    setGearShorts(updated);
  };

  const updateSocks = (index: number, size: string) => {
    const updated = [...gearSocks];
    updated[index] = { size };
    setGearSocks(updated);
  };

  // Smart pricing: auto-detect kit combos
  const isKitCombo =
    gearTshirts.length > 0 && gearShorts.length > 0 && gearSocks.length > 0;

  const calculateGearTotal = (): number => {
    if (!wantsGear) return 0;
    let total = 0;
    if (isKitCombo) {
      total += kitPrice || 0;
      const extraShirts = Math.max(0, gearTshirts.length - 1);
      total += extraShirts * (tshirtPrice || 0);
      const extraShorts = Math.max(0, gearShorts.length - 1);
      total += extraShorts * (shortsPrice || 0);
    } else {
      total += gearTshirts.length * (tshirtPrice || 0);
      total += gearShorts.length * (shortsPrice || 0);
    }
    return total;
  };

  const computeGearOption = () => {
    if (!wantsGear) return "none";
    if (isKitCombo) return "full-kit";
    if (gearTshirts.length > 0 && gearShorts.length === 0) return "tshirt-only";
    if (gearShorts.length > 0 && gearTshirts.length === 0) return "shorts-only";
    return "full-kit";
  };

  const handleApplyVoucher = async () => {
    if (!voucherCode.trim()) return;
    setVoucherStatus("checking");
    setVoucherMessage("");
    try {
      const response = await apiRequest("/api/discount-codes/validate", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${playerToken}`,
        },
        body: JSON.stringify({
          code: voucherCode.trim(),
          scope: "season",
          scopeTargetName: seasonName,
        }),
      });
      const data = await response.json();
      if (response.ok && data.valid) {
        setVoucherStatus("valid");
        setVoucherMessage(`${data.label} applied!`);
      } else {
        setVoucherStatus("invalid");
        setVoucherMessage(data.error || "Invalid code");
      }
    } catch (err: any) {
      setVoucherStatus("invalid");
      setVoucherMessage(err.message || "Failed to validate code");
    }
  };

  const handleSubmit = async () => {
    setError("");
    setLoading(true);

    try {
      // Get projected division to store with registration
      const projected = getProjectedDivision();

      const gearTotalCost = calculateGearTotal();
      const gearOption = computeGearOption();

      const registrationData: ExistingPlayerRegistrationRequest = {
        email: (email || player?.email || "").trim(),
        phone: (phone || player?.phone || "").trim(),
        nationality: nationality.trim() || undefined,
        gender: gender || undefined,
        isDPVRegistered,
        wantsGear,
        gearOption: gearOption as any,
        gearTotalCost,
        wantsTrialDay,
        preferredDivision:
          !isTournamentOnly && wantsTrialDay && preferredDivision
            ? preferredDivision.trim()
            : undefined,
        preferredDivisionReason:
          !isTournamentOnly && wantsTrialDay && preferredDivisionReason
            ? preferredDivisionReason.trim()
            : undefined,
        projectedDivision: isTournamentOnly ? undefined : projected?.newDivision,
        projectionType: isTournamentOnly ? undefined : projected?.type,
        currentDivision: isTournamentOnly ? undefined : projected?.currentDivision,
        ...(isTournamentOnly
          ? {
              preferredDivisionLevel,
              levelReason: ["intermediate", "advanced", "pro"].includes(
                preferredDivisionLevel,
              )
                ? levelReason.trim() || undefined
                : undefined,
              playtomicLevel: playtomicLevel.trim() || undefined,
              hasTournamentExperience,
              hearAboutUsSource: hearAboutUsSource.trim() || undefined,
              hasReferencePlayer,
              referencePlayerName: hasReferencePlayer
                ? referencePlayerName.trim() || undefined
                : undefined,
            }
          : {}),
        gearTshirts:
          wantsGear && gearTshirts.length > 0 ? gearTshirts : undefined,
        gearShorts: wantsGear && gearShorts.length > 0 ? gearShorts : undefined,
        gearSocks: wantsGear && gearSocks.length > 0 ? gearSocks : undefined,
        voucherCode:
          voucherStatus === "valid" ? voucherCode.trim() : undefined,
      };

      const response = await apiRequest(
        "/api/season-registration/submit-existing",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${playerToken}`,
          },
          body: JSON.stringify(registrationData),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to submit registration");
      }

      setSuccess(true);
    } catch (err: any) {
      setError(err.message || "Failed to submit registration");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      const wasSuccess = success;
      onClose();
      setError("");
      setEmail("");
      setPhone("");
      setIsDPVRegistered(false);
      setWantsGear(false);
      setWantsTrialDay(false);
      setPreferredDivision("");
      setGearTshirts([]);
      setGearShorts([]);
      setGearSocks([]);
      setVoucherCode("");
      setVoucherStatus("idle");
      setVoucherMessage("");
      setSuccess(false);
      if (wasSuccess && onSuccess) {
        onSuccess();
      }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <span style={{ fontSize: "1.5rem" }}>🎾</span>
          <Typography variant="h6">Register for Next Season</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {success ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Registration submitted successfully!
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              We are now gathering all interested players. We will then
              distribute the players into their corresponding divisions and
              reach out to each player at their provided email.
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Yours is: <strong>{email || player?.email}</strong>
              <br />
              <Typography component="span" variant="caption" color="text.secondary">
                (If the email is wrong, please reach out to us)
              </Typography>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              We will reach out to you informing you of the division you have
              been placed in, whether you were selected for the trial day, and
              the payment info.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              After payment, you are confirmed in the SPL season — your account
              will be activated on the site and you will be added to the
              WhatsApp group.
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="body2" sx={{ mb: 1 }}>
              If you have any questions, reach out to us:
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 3,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <WhatsAppIcon sx={{ color: "success.main" }} />
                <Typography variant="body2">+49 176 20193968</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <EmailIcon color="action" />
                <Typography variant="body2">stuttgartpadel@gmail.com</Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Secure your spot in the next season! Your profile information will
              be automatically included.
            </Typography>

            {(!player?.email || !player?.phone) && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  We need the following information to complete your
                  registration:
                </Typography>
                <Box component="ul" sx={{ mt: 0, mb: 1, pl: 2 }}>
                  {!player?.email && (
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Email:</strong> To inform you if you got a place
                      in next season's league
                    </Typography>
                  )}
                  {!player?.phone && (
                    <Typography component="li" variant="body2">
                      <strong>Phone:</strong> To add you to the WhatsApp group
                      of your division
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  This information will be saved to your profile and used in
                  accordance with the{" "}
                  <MuiLink
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontWeight: "bold" }}
                  >
                    Privacy Policy
                  </MuiLink>{" "}
                  you previously accepted when registering your account.
                </Typography>
              </Alert>
            )}

            <Box sx={{ mb: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Account Name:</strong> {player?.username || "N/A"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Player Name:</strong> {player?.name || "N/A"}
              </Typography>
              {player?.email && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Email:</strong> {player.email}
                </Typography>
              )}
              {player?.phone && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Phone:</strong> {player.phone}
                </Typography>
              )}
            </Box>

            {isTournamentOnly ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Since your account has no league division history yet,
                    please answer a few questions so we can place you in the
                    right division. New players normally enter with level{" "}
                    <strong>Beginner</strong> or <strong>Beginner+</strong>.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Note: This doesn't guarantee you being placed in that
                    division - final placement is determined by management.
                  </Typography>
                </Alert>

                <FormControl fullWidth required>
                  <InputLabel>Which level would you prefer?</InputLabel>
                  <Select
                    value={preferredDivisionLevel}
                    label="Which level would you prefer?"
                    onChange={(e) => {
                      const val = e.target.value as
                        | "beginner"
                        | "beginner+"
                        | "intermediate"
                        | "advanced"
                        | "pro";
                      setPreferredDivisionLevel(val);
                      if (!["intermediate", "advanced", "pro"].includes(val)) {
                        setLevelReason("");
                      }
                    }}
                  >
                    <MenuItem value="beginner">🟢 Beginner</MenuItem>
                    <MenuItem value="beginner+">🔵 Beginner+</MenuItem>
                    <MenuItem value="intermediate">🟡 Intermediate</MenuItem>
                    <MenuItem value="advanced">🟠 Advanced</MenuItem>
                    <MenuItem value="pro">🔴 Pro</MenuItem>
                  </Select>
                </FormControl>

                {["intermediate", "advanced", "pro"].includes(
                  preferredDivisionLevel,
                ) && (
                  <TextField
                    label={`Why do you think you should be placed at ${preferredDivisionLevel} level?`}
                    value={levelReason}
                    onChange={(e) => setLevelReason(e.target.value)}
                    required
                    fullWidth
                    multiline
                    minRows={3}
                    helperText="Please explain your padel experience/background that supports this level"
                  />
                )}

                <TextField
                  label="Playtomic Level (optional)"
                  value={playtomicLevel}
                  onChange={(e) => setPlaytomicLevel(e.target.value)}
                  fullWidth
                  helperText="e.g., 3.5 - if you have a Playtomic profile with a level"
                />

                <Box>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                    Did you participate in any tournaments?
                  </Typography>
                  <ToggleButtonGroup
                    value={hasTournamentExperience ? "yes" : "no"}
                    exclusive
                    onChange={(_, val) => {
                      if (val) setHasTournamentExperience(val === "yes");
                    }}
                    size="small"
                  >
                    <ToggleButton value="yes" sx={{ px: 3, textTransform: "none", fontWeight: 600 }}>
                      Yes
                    </ToggleButton>
                    <ToggleButton value="no" sx={{ px: 3, textTransform: "none", fontWeight: 600 }}>
                      No
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <TextField
                  label="Where did you hear about the Stuttgart Padel League from?"
                  value={hearAboutUsSource}
                  onChange={(e) => setHearAboutUsSource(e.target.value)}
                  fullWidth
                  placeholder="e.g., Instagram, a friend, a padel club, ..."
                />

                <Box>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                    Do you play regularly with a player from SPL that we can
                    ask your level from?
                  </Typography>
                  <ToggleButtonGroup
                    value={hasReferencePlayer ? "yes" : "no"}
                    exclusive
                    onChange={(_, val) => {
                      if (val) {
                        setHasReferencePlayer(val === "yes");
                        if (val !== "yes") setReferencePlayerName("");
                      }
                    }}
                    size="small"
                  >
                    <ToggleButton value="yes" sx={{ px: 3, textTransform: "none", fontWeight: 600 }}>
                      Yes
                    </ToggleButton>
                    <ToggleButton value="no" sx={{ px: 3, textTransform: "none", fontWeight: 600 }}>
                      No
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {hasReferencePlayer && (
                  <TextField
                    label="Name of the SPL player who can vouch for your level"
                    value={referencePlayerName}
                    onChange={(e) => setReferencePlayerName(e.target.value)}
                    required
                    fullWidth
                  />
                )}

                <Box
                  sx={{
                    p: 2,
                    bgcolor: "rgba(118,75,162,0.08)",
                    borderRadius: 2,
                    border: "2px solid rgba(118,75,162,0.2)",
                  }}
                >
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                    🏆 If we are still not sure about your level, are you okay
                    to participate in a trial day so that we can evaluate your
                    level?
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    If not, then we evaluate the level based on our knowledge
                    of you.
                  </Typography>
                  <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1.5, fontStyle: "italic" }}>
                    Note: Trial days are counted from your contingent of paid
                    league days.
                  </Typography>
                  <ToggleButtonGroup
                    value={wantsTrialDay ? "yes" : "no"}
                    exclusive
                    onChange={(_, val) => {
                      if (val) setWantsTrialDay(val === "yes");
                    }}
                    size="small"
                  >
                    <ToggleButton
                      value="yes"
                      sx={{
                        px: 3,
                        textTransform: "none",
                        fontWeight: 600,
                        "&.Mui-selected": {
                          color: "#764ba2",
                          bgcolor: "rgba(118,75,162,0.15)",
                        },
                      }}
                    >
                      Yes
                    </ToggleButton>
                    <ToggleButton value="no" sx={{ px: 3, textTransform: "none", fontWeight: 600 }}>
                      No
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>
            ) : (
              <>
            {/* Projected Division for Next Season */}
            {projectedDivision && (
              <Alert
                severity={
                  projectedDivision.type === "promoted"
                    ? "success"
                    : projectedDivision.type === "relegated"
                      ? "warning"
                      : "info"
                }
                icon={
                  projectedDivision.type === "promoted" ? (
                    <TrendingUpIcon />
                  ) : projectedDivision.type === "relegated" ? (
                    <TrendingDownIcon />
                  ) : (
                    <RemoveIcon />
                  )
                }
                sx={{ mb: 2 }}
              >
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Your Projected Division for Next Season
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                    mb: 1,
                  }}
                >
                  <Chip
                    label={projectedDivision.currentDivision}
                    size="small"
                    color="default"
                  />
                  {projectedDivision.type === "promoted" && (
                    <>
                      <TrendingUpIcon fontSize="small" color="success" />
                      <Chip
                        label={projectedDivision.newDivision}
                        size="small"
                        color="success"
                      />
                    </>
                  )}
                  {projectedDivision.type === "relegated" && (
                    <>
                      <TrendingDownIcon fontSize="small" color="warning" />
                      <Chip
                        label={projectedDivision.newDivision}
                        size="small"
                        color="warning"
                      />
                    </>
                  )}
                  {projectedDivision.type === "stays" && (
                    <>
                      <RemoveIcon fontSize="small" />
                      <Chip label="Same Division" size="small" color="info" />
                    </>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Based on your current position:{" "}
                  <strong>#{projectedDivision.position}</strong> of{" "}
                  {projectedDivision.totalPlayers} players
                  {projectedDivision.type === "promoted" &&
                    ` (Promotion Zone - Top ${getEffectivePromotionSpots(projectedDivision.currentDivision, aboveDivisionRankings)})`}
                  {projectedDivision.type === "relegated" &&
                    (projectedDivision.relegationReason === "lowWinRate"
                      ? " (Relegation Zone - below 40% win rate)"
                      : ` (Relegation Zone - Bottom ${getRelegationSpots(projectedDivision.currentDivision)})`)}
                </Typography>
                {projectedDivision.type === "promoted" &&
                  getEffectivePromotionSpots(
                    projectedDivision.currentDivision,
                    aboveDivisionRankings,
                  ) > getPromotionSpots(projectedDivision.currentDivision) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block" }}
                    >
                      Note: {projectedDivision.newDivision} has extra spots
                      open this season since more than the usual number of
                      players were relegated out of it (some below 40% win
                      rate).
                    </Typography>
                  )}
              </Alert>
            )}

            {activePhase !== "mid_season" && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={wantsTrialDay}
                      onChange={(e) => setWantsTrialDay(e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        I am not satisfied with the projected division and
                        would like to play in the trial day to prove I should
                        play in another division 🏆
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        The trial day is an opportunity to prove you should
                        play in a different division than projected above.
                        Places in the trial days are limited so priority goes
                        to new players instead of players who already
                        participated in the league. Please understand that
                        your interest in participation doesn't guarantee
                        participation.
                      </Typography>
                      <Typography
                        variant="caption"
                        color="warning.main"
                        sx={{ display: "block", mt: 1 }}
                      >
                        ⚠️ Note: Participation in the trial day uses one of
                        the free league days paid for when registering.
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 2, alignItems: "flex-start" }}
                />

                {wantsTrialDay && (
                  <>
                    <TextField
                      label="Which division do you think you should play in?"
                      placeholder="e.g., Division 1, Division 2, etc."
                      value={preferredDivision}
                      onChange={(e) => setPreferredDivision(e.target.value)}
                      fullWidth
                      sx={{ mb: 2, ml: 4 }}
                      helperText="Please specify the division you believe matches your skill level"
                    />
                    <TextField
                      label={`Why I think I should play in ${preferredDivision || "this division"}`}
                      placeholder="Explain why you believe you should play in a different division"
                      value={preferredDivisionReason}
                      onChange={(e) => setPreferredDivisionReason(e.target.value)}
                      fullWidth
                      multiline
                      minRows={3}
                      sx={{ mb: 2, ml: 4 }}
                    />
                  </>
                )}
              </>
            )}
              </>
            )}

            {!player?.email && (
              <TextField
                fullWidth
                label="Email Address *"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: 2 }}
                required
                helperText="To inform you about your place in next season's league"
              />
            )}

            {!player?.phone && (
              <TextField
                fullWidth
                label="Phone Number *"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                sx={{ mb: 2 }}
                required
                helperText="To add you to the WhatsApp group of your division"
              />
            )}

            <TextField
              fullWidth
              label="Nationality"
              value={nationality}
              onChange={(e) => setNationality(e.target.value)}
              sx={{ mb: 2 }}
              helperText="🏳️ This will be used as a flag on your shirt if you order gear"
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="gender-select-label">Gender</InputLabel>
              <Select
                labelId="gender-select-label"
                label="Gender"
                value={gender}
                onChange={(e) => setGender(e.target.value as Gender)}
              >
                <MenuItem value="Man">Man</MenuItem>
                <MenuItem value="Woman">Woman</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={isDPVRegistered}
                  onChange={(e) => setIsDPVRegistered(e.target.checked)}
                />
              }
              label="I am registered with the Deutscher Padel Verband (DPV)"
              sx={{ mb: 1 }}
            />

            {/* Gear Selection */}
            {gearAllowed && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={wantsGear}
                    onChange={(e) => {
                      setWantsGear(e.target.checked);
                      if (e.target.checked && gearTshirts.length === 0) {
                        setGearTshirts([
                          {
                            size: "M",
                            shirtType: "tshirt",
                            nameOnShirt: "",
                          },
                        ]);
                      }
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      I want to order official{" "}
                      <strong>Stuttgart Padel League gear</strong> 🎽
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      Note: After the issues we had in the Summer Season with
                      the T-shirt orders, we decided to go back to the old
                      sponsor, as the quality and delivery time were
                      guaranteed and we didn't have the printing problems or
                      sizing problems.
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2, alignItems: "flex-start" }}
              />
            )}

            {gearAllowed && wantsGear && (
              <Box
                sx={{ mb: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}
              >
                <Alert severity="info" sx={{ mb: 2 }}>
                  💡 <strong>Smart pricing:</strong> Order a shirt + shorts/skirt +
                  socks together and save! Kit:{" "}
                  {kitPrice ? `${kitPrice}€` : "TBD"} vs individual shirt (
                  {tshirtPrice ? `${tshirtPrice}€` : "TBD"}) + shorts/skirt+socks (
                  {shortsPrice ? `${shortsPrice}€` : "TBD"}).
                </Alert>

                {/* Shirts section */}
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  👕 Shirts
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Choose T-Shirt or Polo for each item
                </Typography>

                {gearTshirts.map((tshirt, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 2,
                      p: 1.5,
                      bgcolor: "background.paper",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <ToggleButtonGroup
                        value={tshirt.shirtType || "tshirt"}
                        exclusive
                        onChange={(_, val) => {
                          if (val) updateTshirt(index, "shirtType", val);
                        }}
                        size="small"
                      >
                        <ToggleButton
                          value="tshirt"
                          sx={{ px: 2, textTransform: "none", fontWeight: 600 }}
                        >
                          👕 T-Shirt
                        </ToggleButton>
                        <ToggleButton
                          value="polo"
                          sx={{ px: 2, textTransform: "none", fontWeight: 600 }}
                        >
                          🏇 Polo
                        </ToggleButton>
                      </ToggleButtonGroup>
                      <IconButton
                        onClick={() => removeTshirt(index)}
                        color="error"
                        disabled={gearTshirts.length === 1}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <GearDesignPicker
                        designs={
                          tshirt.shirtType === "polo"
                            ? poloDesigns
                            : tshirtDesigns
                        }
                        selectedId={tshirt.designId}
                        onSelect={(designId) =>
                          updateTshirtDesign(index, designId)
                        }
                        label="Design"
                        sizeChartUrl={
                          tshirt.shirtType === "polo"
                            ? poloSizeChartUrl
                            : tshirtSizeChartUrl
                        }
                      />
                      <FormControl sx={{ minWidth: 80 }}>
                        <InputLabel>Size</InputLabel>
                        <Select
                          value={tshirt.size}
                          onChange={(e) =>
                            updateTshirt(index, "size", e.target.value)
                          }
                          label="Size"
                          size="small"
                        >
                          <MenuItem value="S">S</MenuItem>
                          <MenuItem value="M">M</MenuItem>
                          <MenuItem value="L">L</MenuItem>
                          <MenuItem value="XL">XL</MenuItem>
                          <MenuItem value="XXL">XXL</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        label="Name on Shirt (Optional)"
                        value={tshirt.nameOnShirt || ""}
                        onChange={(e) =>
                          updateTshirt(index, "nameOnShirt", e.target.value)
                        }
                        size="small"
                        inputProps={{ maxLength: 20 }}
                      />
                    </Box>
                  </Box>
                ))}

                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addTshirt}
                  size="small"
                  sx={{ mb: 3 }}
                >
                  Add Another Shirt
                </Button>

                <Divider sx={{ my: 2 }} />

                {/* Shorts section */}
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  🩳 Shorts/Skirt + Socks (optional)
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Each shorts comes with a pair of socks. Add shorts to combine
                  with a shirt for kit pricing.
                </Typography>

                {gearShorts.length === 0 ? (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addShorts}
                    size="small"
                    sx={{ mb: 2 }}
                  >
                    Add Shorts/Skirt + Socks
                  </Button>
                ) : (
                  <>
                    {gearShorts.map((shorts, index) => (
                      <Box
                        key={index}
                        sx={{
                          mb: 2,
                          p: 1.5,
                          bgcolor: "background.paper",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <GearDesignPicker
                          designs={shortsDesigns}
                          selectedId={shorts.designId}
                          onSelect={(designId) =>
                            updateShortsDesign(index, designId)
                          }
                          label="Design"
                          sizeChartUrl={shortsSizeChartUrl}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <FormControl sx={{ flex: 1 }}>
                            <InputLabel>Shorts Size</InputLabel>
                            <Select
                              value={shorts.size}
                              onChange={(e) =>
                                updateShorts(index, e.target.value as any)
                              }
                              label="Shorts Size"
                              size="small"
                            >
                              <MenuItem value="S">S</MenuItem>
                              <MenuItem value="M">M</MenuItem>
                              <MenuItem value="L">L</MenuItem>
                              <MenuItem value="XL">XL</MenuItem>
                              <MenuItem value="XXL">XXL</MenuItem>
                            </Select>
                          </FormControl>
                          <IconButton
                            onClick={() => removeShorts(index)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                        <TextField
                          fullWidth
                          label="🧦 Socks - Shoe Size"
                          value={gearSocks[index]?.size || ""}
                          onChange={(e) => updateSocks(index, e.target.value)}
                          size="small"
                          placeholder="e.g., 42"
                          inputProps={{
                            maxLength: 2,
                            pattern: "[0-9]*",
                          }}
                          helperText="Included with shorts"
                        />
                      </Box>
                    ))}

                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={addShorts}
                      size="small"
                      sx={{ mb: 2 }}
                    >
                      Add Another Shorts/Skirt + Socks
                    </Button>
                  </>
                )}

                {/* Smart pricing display */}
                {(gearTshirts.length > 0 || gearShorts.length > 0) && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    {isKitCombo && (
                      <Alert severity="success" sx={{ mb: 1 }}>
                        🎉 <strong>Kit discount applied!</strong> First shirt +
                        shorts/skirt + socks bundled at {kitPrice}€.
                        {(gearTshirts.length > 1 || gearShorts.length > 1) && (
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{ mt: 0.5 }}
                          >
                            {gearTshirts.length > 1 &&
                              `+ ${gearTshirts.length - 1} extra shirt(s) at ${tshirtPrice}€ each`}
                            {gearTshirts.length > 1 &&
                              gearShorts.length > 1 &&
                              " | "}
                            {gearShorts.length > 1 &&
                              `+ ${gearShorts.length - 1} extra shorts at ${shortsPrice}€ each`}
                          </Typography>
                        )}
                      </Alert>
                    )}

                    <Alert severity="info">
                      💰 Estimated Gear Total:{" "}
                      <strong>{calculateGearTotal()}€</strong>
                    </Alert>
                  </>
                )}
              </Box>
            )}

            {/* Voucher Code */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Have a voucher code?
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <TextField
                  size="small"
                  placeholder="e.g. SPL-7K2M"
                  value={voucherCode}
                  onChange={(e) => {
                    setVoucherCode(e.target.value.toUpperCase());
                    setVoucherStatus("idle");
                    setVoucherMessage("");
                  }}
                  disabled={voucherStatus === "valid"}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="outlined"
                  onClick={handleApplyVoucher}
                  disabled={
                    !voucherCode.trim() ||
                    voucherStatus === "checking" ||
                    voucherStatus === "valid"
                  }
                  startIcon={
                    voucherStatus === "checking" ? (
                      <CircularProgress size={16} />
                    ) : undefined
                  }
                >
                  {voucherStatus === "valid" ? "Applied" : "Apply"}
                </Button>
              </Box>
              {voucherMessage && (
                <Typography
                  variant="caption"
                  color={voucherStatus === "valid" ? "success.main" : "error"}
                  sx={{ display: "block", mt: 0.5 }}
                >
                  {voucherStatus === "valid" ? "🎉 " : ""}
                  {voucherMessage}
                </Typography>
              )}
            </Box>

            {/* Estimated Price Information */}
            {activePhase === "mid_season" ? (
              midSeasonPrice && (
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    bgcolor: "info.light",
                    borderRadius: 1,
                    border: "2px solid",
                    borderColor: "info.main",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    gutterBottom
                    sx={{ color: "info.dark" }}
                  >
                    💶 Price Information
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.primary" }}>
                    Price for joining the remaining half of this season is{" "}
                    <strong>{midSeasonPrice} Euro</strong>. This
                    should be paid first after we approve the registration.
                    More info will be sent to you on your email including
                    payment info.
                  </Typography>
                </Box>
              )
            ) : (
              estimatedPrice && (
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    bgcolor: "info.light",
                    borderRadius: 1,
                    border: "2px solid",
                    borderColor: "info.main",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    gutterBottom
                    sx={{ color: "info.dark" }}
                  >
                    💶 Price Information
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.primary" }}>
                    Estimated price for this season will be around{" "}
                    <strong>{estimatedPrice} Euro</strong> incl. 5 free league
                    days. This should be paid first after we
                    approve the registration. More info will be sent to you on
                    your email including payment info and the exact price.
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, fontStyle: "italic", color: "text.secondary" }}
                  >
                    The price might change slightly depending on the number of
                    registrations we receive for next season.
                  </Typography>
                </Box>
              )
            )}
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          flexDirection: "column",
          alignItems: "stretch",
          gap: 1,
          px: 3,
          pb: 2,
        }}
      >
        {success ? (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={handleClose} variant="contained">
              Close
            </Button>
          </Box>
        ) : (
          <>
            {(() => {
              const missing: string[] = [];
              if (!email && !player?.email) missing.push("Email");
              if (!phone && !player?.phone) missing.push("Phone");
              if (
                wantsGear &&
                gearTshirts.length === 0 &&
                gearShorts.length === 0
              )
                missing.push("At least one gear item");
              if (wantsGear && gearSocks.some((s) => !s.size))
                missing.push("Shoe size for all socks");
              if (
                wantsGear &&
                (gearTshirts.some((t) => !t.designId) ||
                  gearShorts.some((s) => !s.designId))
              )
                missing.push("A design for each gear item");
              return missing.length > 0 ? (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  Please fill in: {missing.join(", ")}
                </Alert>
              ) : null;
            })()}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={
                  loading ||
                  (!email && !player?.email) ||
                  (!phone && !player?.phone) ||
                  (wantsGear &&
                    gearTshirts.length === 0 &&
                    gearShorts.length === 0) ||
                  (wantsGear && gearSocks.some((s) => !s.size)) ||
                  (wantsGear &&
                    (gearTshirts.some((t) => !t.designId) ||
                      gearShorts.some((s) => !s.designId)))
                }
                startIcon={loading && <CircularProgress size={16} />}
              >
                {loading ? "Submitting..." : "Register"}
              </Button>
            </Box>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
